import {
  Component,
  computed,
  effect,
  ElementRef,
  HostListener,
  inject,
  input,
  Input,
  model,
  output,
  ViewChild,
} from '@angular/core';
import { NgClass } from '@angular/common';
import { WaMutationObserver } from '@ng-web-apis/mutation-observer';
import { ExtractedLocationStoreService } from '../../../service/overlay/extracted-location-store.service';
import { FormArray } from '@angular/forms';
import { TableRowComponent } from './table-item/table-row.component';
import { ExtractedRowForm, ExtractedTableCellForm } from '../../../models/extraction-form-models';
import { CurrentTableCell, TableDataCapturingConfig } from '../../../models/interfaces';
import { PersistDataService } from '../../../service/app-state/persist-data.service';
import { isTableReference } from '../../../models/extracted-values';
import { OcrHighlightService } from '../../../service/overlay/ocr-highlight.service';

@Component({
  selector: 'ct-extraction-table',
  imports: [NgClass, WaMutationObserver, TableRowComponent],
  templateUrl: './table.component.html',
})
export class TableComponent {
  @Input({ required: true }) tableRows = new FormArray<ExtractedRowForm>([]);
  @Input({ required: true }) isClassificationView = false;

  @Input() isTableLayoutEditable = true;
  readonly capturingConfig = input.required<TableDataCapturingConfig>();
  @Input() hasActionButtonInRows = false;

  readonly currentRowToClassify = model<ExtractedRowForm | undefined>(undefined);

  /**
   * By "active" we mean that the section this table is part of was focused last.
   */
  readonly isActive = input<boolean>(false);

  rowAdded = output<{ index: number; tableId: string | undefined }>();
  rowDeleted = output<number>();
  currentTableCellChange = output<CurrentTableCell>();
  rowSelected = output<number | undefined>();

  @ViewChild('table') table!: ElementRef<HTMLDivElement>;

  private readonly extractedLocationStoreService = inject(ExtractedLocationStoreService);
  private readonly persistDataService = inject(PersistDataService);
  private readonly highlightService = inject(OcrHighlightService);

  protected readonly tableModel = computed(() => this.capturingConfig().tableModel);

  readonly currentFieldIndex = model<number | undefined>(0);

  protected readonly currentTableCell = computed(() => {
    const fieldIndex = this.currentFieldIndex();
    if (fieldIndex === undefined) return undefined;

    const fieldModels = this.visibleFieldModels();
    const columnCount = fieldModels.length;

    const columnIndex = fieldIndex % columnCount;
    const rowIndex = Math.floor(fieldIndex / columnCount);

    const fieldModel = fieldModels[columnIndex];
    const fieldKey = fieldModel.key;

    const field = this.tableRows.at(rowIndex).controls.rowData.controls[fieldKey];

    return { fieldModel, field };
  });

  /**
   * In a normal (table) extraction view, we display all columns that are not inline consolidators,
   * as opposed to normal field group inline consolidators which we show inside the extraction view.
   * Instead, we show inline consolidators of table fields during classification view (but this is
   * explicitly defined in the classification config).
   *
   * Note that the term "row classification" does not make much sense anymore, since its at least
   * "inline-consolidation-and-row-classification" and its probably better to just handle the
   * "row class" as a normal field in the future (as we are doing for "hauptdiagnose" and
   * "nebendiagnose" for 301-tables).
   */
  protected readonly visibleFieldModels = computed(() => {
    const tableModel = this.tableModel();
    if (this.isClassificationView) {
      return tableModel.rowClassification?.visibleFieldModels ?? tableModel.fieldModels;
    }

    return tableModel.fieldModels.filter((fieldModel) => !fieldModel.inlineConsolidatorForField);
  });

  protected readonly labelsAndWidths = computed(() =>
    this.visibleFieldModels().map((model) => [model.label.text, model.widthUtilityClass] as const),
  );

  private readonly documentId = computed(() => this.capturingConfig()?.document?.id);

  constructor() {
    effect(() => {
      const currentTableCell = this.currentTableCell();
      if (!currentTableCell) return;

      const { fieldModel, field } = currentTableCell;

      if (!this.visibleFieldModels().includes(fieldModel)) {
        return;
      }

      this.currentTableCellChange.emit({
        fieldKey: fieldModel.key,
        cellForm: field,
      });

      const currentRowToClassifyMembership =
        this.currentRowToClassify()?.controls?.rowMembership?.getRawValue();
      const documentId = this.documentId();

      if (
        this.isClassificationView &&
        currentRowToClassifyMembership &&
        isTableReference(currentRowToClassifyMembership) &&
        currentRowToClassifyMembership.originalTableRowIndex !== undefined
      ) {
        this.highlightService.setTableRowHighlight(
          currentRowToClassifyMembership.tableId,
          currentRowToClassifyMembership.originalTableRowIndex,
        );
      } else if (documentId) {
        this.highlightService.setFieldHighlight(documentId, field.getRawValue().id);
      }
    });
  }

  getTableElement(): HTMLDivElement {
    return this.table.nativeElement;
  }

  setNextField() {
    if (this.currentFieldIndex() === undefined) return;
    (this.getInputElement(this.currentFieldIndex()!)! as HTMLElement)?.blur();

    const nextFieldIndex = this.getNextFieldIndex();

    const noNextField = nextFieldIndex === -1;
    if (noNextField) {
      this.rowAdded.emit({ index: this.tableRows.length, tableId: undefined });
      this.activateTableFieldAtIndex(
        this.getExtractedTableCells(this.tableRows).length - this.visibleFieldModels().length,
      );

      return;
    }

    this.activateTableFieldAtIndex(nextFieldIndex!);
  }

  protected activateTableFieldAtIndex(index: number) {
    this.currentFieldIndex.set(index);
  }

  getExtractedTableCells(tableRows: FormArray<ExtractedRowForm>): ExtractedTableCellForm[] {
    return tableRows.controls.flatMap((rowFormGroup) =>
      Object.entries(rowFormGroup.controls.rowData.controls)
        .filter(([fieldKey, _]) =>
          this.visibleFieldModels().some((model) => model.key === fieldKey),
        )
        .map(([, form]) => form),
    );
  }

  getNextFieldIndex(): number | undefined {
    if (this.currentFieldIndex() === undefined) return undefined;

    const nextFieldIndex = this.getExtractedTableCells(this.tableRows)
      .slice(this.currentFieldIndex()! + 1)
      .findIndex((field) => {
        return !field.controls.value.value || field.controls.value.invalid;
      });

    if (nextFieldIndex === -1) return nextFieldIndex;

    return nextFieldIndex + this.currentFieldIndex()! + 1;
  }

  onTableChange(): void {
    if (this.currentFieldIndex() === undefined) return;
    if (this.getNextFieldIndex() === -1) return;

    const element = this.getInputElement(this.currentFieldIndex()!);
    if (!element) return;

    // Above the input check: only currency cells are inputs, the rest are contenteditable.
    element.closest('.row')?.scrollIntoView({ block: 'nearest' });

    if (!element.matches('input')) return;

    const input = element as HTMLInputElement;
    input.focus({ preventScroll: true });
    input.setSelectionRange(0, 0);
    input.scrollLeft = 0;
  }

  private getInputElement(index: number): Element | undefined {
    // TODO: select nth - child?
    return this.getTableElement().querySelectorAll('.table-input')[index];
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {
    if (this.isActive() && event.key === 'Enter') {
      this.setNextField();
    }

    if (this.isActive() && event.key === 'Escape') {
      this.currentFieldIndex.set(undefined);
    }
  }

  protected onManualValueChange(manualChangeEvent: { fieldId: string }) {
    const documentId = this.documentId();
    if (!documentId) return;

    this.extractedLocationStoreService.deleteExtractedLocation(
      documentId,
      manualChangeEvent.fieldId,
    );
    this.persistDataService.flagExistingManualChanges(documentId);
    this.highlightService.clearHighlight();
  }

  protected onTriggerTableCallback({
    currentRowForm,
    classValue,
  }: {
    currentRowForm: ExtractedRowForm;
    classValue: string | undefined;
  }) {
    this.tableModel().rowClassification!.tableCallbackProvider?.(
      this.tableRows.controls,
      currentRowForm,
      classValue ?? '',
    );
  }

  protected updateFieldIndex(index: number | undefined) {
    // hack: set the index to undefined first to force recomputing the currentTableCell signal
    this.currentFieldIndex.set(undefined);
    this.currentFieldIndex.set(index);
  }
}
